import React, { useContext, useEffect, useRef } from 'react';

import { AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { Draggable } from 'gsap/dist/Draggable';
import { ThemeContext } from 'styled-components';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { setManagerStopDragging } from '@app/store/actions/nft-manager';

import { type NFTManagerQueryType } from '../../hooks';
import { getItemById } from '../../utils';
import { NftAsset } from '../SidePanelContent/components/ManagerGrid/components/NftCard/components';

import { DraggingWrapper, ItemWrapper } from './styles';

const CURSOR_POSITION_X = 10;
const CURSOR_POSITION_Y = 15;

gsap.registerPlugin(Draggable);

type DraggingProps = {
    query: NFTManagerQueryType;
};
export function Dragging({ query }: DraggingProps) {
    const theme = useContext(ThemeContext);
    const dispatch = useAppDispatch();
    const { data } = query();

    const draggingItems = useAppSelector((state) => state.nftManager.draggingItems);
    const draggingInitialMouseEvent = useAppSelector((state) => state.nftManager.draggingInitialMouseEvent);

    const draggableRef = useRef<Draggable>();
    const draggingWrapperRef = useRef<HTMLDivElement>(null);
    const itemsRef = useRef<{ [key: string]: HTMLDivElement }>({});

    useEffect(() => {
        if (!draggingWrapperRef.current) return;

        const handleRelease = () => {
            if (!draggableRef.current) return;

            // Scale out content on exit (mimic drop)
            gsap.to(draggingWrapperRef.current, {
                scale: 0.8,
                duration: theme.transitions.speeds.fast,
                ease: 'power2.in',
            });

            // Reset dragging events
            dispatch(setManagerStopDragging());
            itemsRef.current = {};
            gsap.set(draggingWrapperRef.current, { pointerEvents: 'none' });
            draggableRef.current.disable();
        };

        // Create unique draggable instance
        draggableRef.current = new Draggable(draggingWrapperRef.current, {
            onRelease: handleRelease,
        });

        return () => {
            draggableRef.current?.kill();
        };
    }, [dispatch, theme]);

    useEffect(() => {
        if (!draggableRef.current || !draggingInitialMouseEvent || !draggingWrapperRef.current) return;

        // Enable dragging events
        draggableRef.current.enable();
        gsap.set(draggingWrapperRef.current, { pointerEvents: 'all' });

        let width = 0;
        draggingItems.forEach((id) => {
            const item = document.getElementById(`draggable-wrapper-${id}`);
            const ref = itemsRef.current[id];
            if (!item || !ref) return;
            const itemWidth = item.offsetWidth;
            if (itemWidth > width) width = itemWidth;

            gsap.to(ref, {
                width,
                x: -CURSOR_POSITION_X,
                y: -CURSOR_POSITION_Y,
                duration: theme.transitions.speeds.normal,
                ease: 'power2.out',
            });
        });

        gsap.set(draggingWrapperRef.current, {
            scale: 1,
            width,
            x: draggingInitialMouseEvent.x,
            y: draggingInitialMouseEvent.y,
        });

        // Start dragging events at cursor
        draggableRef.current.startDrag(draggingInitialMouseEvent);
    }, [theme, draggingInitialMouseEvent, draggingItems]);

    const handleAddRef = (ref: HTMLDivElement, item: string) => {
        if (itemsRef.current) {
            itemsRef.current[item] = ref;
        } else itemsRef.current = { [item]: ref };
    };

    return (
        <DraggingWrapper ref={draggingWrapperRef}>
            <AnimatePresence>
                {draggingItems.map((item, index) => {
                    const itemData = data && getItemById(item, data);

                    if (!itemData) return;

                    return (
                        <ItemWrapper
                            key={item + index}
                            ref={(ref) => ref && handleAddRef(ref, item)}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: theme.transitions.speeds.fast }}
                        >
                            <NftAsset
                                handleAddItem={() => null}
                                handleRemoveItem={() => null}
                                dragged
                                liquidIDSelected={false}
                                onError={() => null}
                                editingLiquidID={false}
                                disabled={false}
                                item={itemData}
                            />
                        </ItemWrapper>
                    );
                })}
            </AnimatePresence>
        </DraggingWrapper>
    );
}
